import { HelpOutline } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { TextField, Tooltip, useMediaQuery } from '@mui/material';
import React, { KeyboardEvent, ChangeEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ServiceList } from './ServiceList';
import { OuterWrapper, TextfieldWrapper, TextInputWrapper, TooltipWrapper } from './Services.styles';
import { ActionPermissions, CustomPermission, ScreenSize, SizeProps } from '../../@types';
import { Button, ButtonVariantProps } from '../../components/atoms/Button';
import { InlineGroup, HorizontalAlignProps } from '../../components/atoms/InlineGroup';
import { Link } from '../../components/atoms/Link';
import { PageContainer } from '../../components/atoms/PageContainer';
import { Typography, TypographyVariantProps } from '../../components/atoms/Typography';
import { PLATFORM_NAME } from '../../configs/common';
import { isUserHasPermission } from '../../configs/permissions';
import { TabHandler } from '../../handlers/TabHandler';
import { useGetUserSystemPermissions } from '../../queries/user-query';
import { Wrapper } from '../../styled/common.styles';

export const Services: React.FC = () => {
  const location = useLocation();

  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [multiSearchString, setMultiSearchString] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const isMobileView = useMediaQuery(ScreenSize.MOBILE);

  useEffect(() => {
    if (multiSearchString) {
      setMultiSearchString('');
      setSearchQuery('');
    }
  }, [location.pathname]);

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  const handleMultiSearch = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      setSearchQuery(multiSearchString);
    }
  };

  TabHandler(`List Services | ${PLATFORM_NAME}`);
  
  return (
    <PageContainer>
      <Wrapper>
        <InlineGroup horizontal={HorizontalAlignProps.Between}>
          <Typography variant={TypographyVariantProps.H5} fontWeight={600}>
            List Services
          </Typography>
          <OuterWrapper isMobileView={isMobileView}>
            {!isMobileView &&
              <TextfieldWrapper isMobileView={isMobileView}>
                <TextInputWrapper>
                  <TextField
                    size={SizeProps.Small}
                    placeholder="Multi-search Services"
                    value={multiSearchString}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (e.target.value === '') {
                        setMultiSearchString(e.target.value);
                        setSearchQuery('');
                      } else setMultiSearchString(e.target.value);
                    }}
                    onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => handleMultiSearch(e)}
                    style={{ width: '250px' }}
                  />
                </TextInputWrapper>
                <TooltipWrapper>
                  <Tooltip
                    title="This multi-search will search Service ID, Service References, Locations (including Sublocations), Location References, Allocations, Clients, Contracts, Brands, and Zones"
                    placement="bottom-start"
                  >
                    <HelpOutline fontSize="small" htmlColor="grey" sx={{ alignItems: 'flex-end' }}/>
                  </Tooltip>
                </TooltipWrapper>
              </TextfieldWrapper>
            }
            {isUserHasPermission(ActionPermissions.Service_Create, permissions) && (
              <Link href="/services/create">
                <Button variant={ButtonVariantProps.Primary} startIcon={<AddIcon />}>
                  Add Service
                </Button>
              </Link>
            )}
          </OuterWrapper>
        </InlineGroup>
        {isMobileView &&
          <TextfieldWrapper isMobileView={isMobileView}>
            <TextInputWrapper>
              <TextField
                size={SizeProps.Small}
                placeholder="Multi-search Services"
                value={multiSearchString}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setMultiSearchString(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => handleMultiSearch(e)}
                fullWidth
              />
            </TextInputWrapper>
            <TooltipWrapper>
              <Tooltip
                title="This multi-search will search Service ID, Service References, Locations (including Sublocations), Location References, Allocations, Clients, Contracts, Brands, and Zones"
                placement="bottom-start"
                enterTouchDelay={10}
                leaveTouchDelay={3000}
              >
                <HelpOutline fontSize="small" htmlColor="grey" sx={{ alignItems: 'flex-end' }}/>
              </Tooltip>
            </TooltipWrapper>
          </TextfieldWrapper>
        }
        {isUserHasPermission(ActionPermissions.Service_Search, permissions) && (
          <ServiceList multiSearchQuery={searchQuery}/>
        )}
      </Wrapper>
    </PageContainer>
  );
};